import { useCallback } from 'react';
import { useMarketDetail } from '../queries/useMarketDetail';
import { useMarketRoute } from '../../../hooks/useMarketRoute';
import { bridge, getNumberRange, isNotNil } from 'shared/src/utils';

export const useLogMarketDefaultAnalyticsParams = (marketId: number) => {
  const { data: marketDetail } = useMarketDetail(marketId);

  return useCallback(() => {
    if (!marketDetail) return false;

    const isExistDownloadableCoupon = () => {
      if (!marketDetail.market.is_exist_usable_coupon) return false;

      if (isNotNil(marketDetail.max_price_coupon)) return true;
      return false;
    };

    const isTodayMarket = () => {
      return marketDetail.market.approved_delivery_types.filter(
        item => item.value === 3,
      );
    };

    return {
      MARKET_NAME: marketDetail.market.name,
      MARKET_SNO: marketDetail.market.sno.toString(),
      FAVORITE_COUNT: marketDetail.market.favorites_count,
      FAVORITE_COUNT_RANGE: getNumberRange({
        rangeUnit: 500,
        target: marketDetail.market.favorites_count,
      }),
      REVIEW_RATING: Math.floor(
        marketDetail.market.purchasing_satisfaction_ratio,
      ),
      MARKET_TYPE: marketDetail.market.market_type.name,
      AGE_TAG1: marketDetail.market.age_tags[0],
      AGE_TAG2: marketDetail.market.age_tags[1],
      STYLE_TAG1: marketDetail.market.style_tags[0],
      STYLE_TAG2: marketDetail.market.style_tags[1],
      IS_EXIST_USABLE_COUPONS: marketDetail.market.is_exist_usable_coupon
        ? 1
        : 0,
      IS_EXIST_DOWNLOADABLE_COUPONS: isExistDownloadableCoupon() ? 1 : 0,
      IS_TODAY_MARKET: isTodayMarket().length > 0 ? 1 : 0,
    };
  }, [marketDetail]);
};

export const useLogMarketAnalyticsEvent = (marketId?: number) => {
  const { params } = useMarketRoute();

  const { data: marketDetail } = useMarketDetail(marketId ?? params.marketId);
  const getDefaultAnalyticsParams = useLogMarketDefaultAnalyticsParams(
    marketId ?? params.marketId,
  );

  return useCallback<
    (params: {
      name: string;
      channel?: Array<
        | 'firebase'
        | 'amplitude'
        | 'braze'
        | 'ablylog'
        | 'facebook'
        | 'airbridge'
      >;
      analytics?: Record<string, unknown>;
    }) => boolean
  >(
    params => {
      if (!marketDetail) return false;

      bridge.logAnalyticsEvent({
        name: params.name,
        channel: params.channel ?? [
          'firebase',
          'facebook',
          'amplitude',
          'braze',
          'ablylog',
        ],
        analytics: {
          SCREEN: 'MARKET',
          ...getDefaultAnalyticsParams(),
          ...params.analytics,
        },
      });

      return true;
    },
    [getDefaultAnalyticsParams, marketDetail],
  );
};
